import { useState, useCallback } from "react";
import Cookies from "js-cookie";
import { toast } from "sonner";

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  validationErrors?: { field?: string; message?: string }[];
}

interface ApiResult<T> {
  state: ApiState<T>;
  fetchData: (
    url: string,
    method?: RequestMethod,
    body?: any,
    customHeaders?: Record<string, string>,
    requiresAuth?: boolean,
    isNotify?: boolean,
    useEngineBaseUrl?: boolean
  ) => Promise<T | null>;
}

const BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "https://engine.plagpro.com";
const ENGINE_BASE_URL =
  process.env.NEXT_PUBLIC_API_ENGINE_BASE_URL || "https://engine.plagpro.com";
const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
};

function useApi<T = any>(
  defaultHeaders: Record<string, string> = {}
): ApiResult<T> {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(
    async (
      url: string,
      method: RequestMethod = "GET",
      body: any = null,
      customHeaders: Record<string, string> = {},
      requiresAuth: boolean = true,
      isNotify: boolean = true,
      useEngineBaseUrl: boolean = false
    ): Promise<T | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const headers: Record<string, string> = {
          ...DEFAULT_HEADERS,
          ...defaultHeaders,
          ...customHeaders,
        };

        // 🔐 Attach Token if required
        if (requiresAuth) {
          const authToken = Cookies.get("authToken");
          if (authToken) {
            headers["Authorization"] = `Bearer ${authToken}`;
          } else {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: "Authentication token required",
            }));
            toast.error("Please log in to continue");
            return null;
          }
        }

        const options: RequestInit = {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
        };

        // 🧾 Handle FormData (multipart)
        if (body instanceof FormData) {
          delete headers["Content-Type"];
          options.body = body;
        }
        const baseUrl = useEngineBaseUrl ? ENGINE_BASE_URL : BASE_URL;
        //        const response = await fetch(
        //   `${useEngineBaseUrl ? ENGINE_BASE_URL : BASE_URL}${url}`,
        //   options
        // );
        const response = await fetch(`${baseUrl}${url}`, options);

        // console.log("base url", baseUrl);

        // console.log(`url [${method}] ${url} → ${response.status}`);

        // 🧱 Handle Unauthorized
        if (response.status === 401) {
          toast.error("Session expired. Please log in again.");
          Cookies.remove("authToken");
          Cookies.remove("user");
          window.location.href = "/login";
          return null;
        }

        // 🧾 Parse JSON response
        const responseData = await response.json().catch(() => ({}));
        // console.log("API Response:", responseData);

        // Case 1: Login success
        if (responseData?.access_token) {
          Cookies.set("authToken", responseData.access_token, { expires: 7 });
          if (isNotify) toast.success("Login successful!");
          setState({ data: responseData, loading: false, error: null });
          return responseData;
        }

        // Case 2: Standard API success
        const { status, data, message, errors } = responseData;
        // ✅ Always treat 2xx responses as success
        if (response.ok) {
          if (isNotify && message) toast.success(message);
          setState({ data: data ?? responseData, loading: false, error: null });
          return data ?? responseData;
        }

        if (!status) {
          const errorMessage =
            responseData.errors?.[0]?.message ||
            responseData.message ||
            responseData.error ||
            responseData.detail.message ||
            "Something went wrong. Please try again..";
          toast.error(errorMessage);
          return null;
        }

        const errorMsg =
          message || "An unexpected error occurred. Please try again.";
        if (isNotify) toast.error(errorMsg);
        setState({
          data: null,
          loading: false,
          error: errorMsg,
          validationErrors: errors ?? [],
        });
        return null;
      } catch (err: any) {
        const errMsg = err.message || "Network error. Please try again.";
        toast.error(errMsg);
        setState({ data: null, loading: false, error: errMsg });
        return null;
      }
    },
    [defaultHeaders]
  );

  return { state, fetchData };
}

export default useApi;
